import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/store';
import { adminGetUsers, adminUpdateUser, adminGetStats } from '../lib/api';
import { Users, DollarSign, Activity, Loader2, Settings, LayoutDashboard, Video, CreditCard, TrendingUp, Shield } from 'lucide-react';

interface User {
  id: string;
  email: string;
  name: string;
  phone: string;
  role: string;
  device_id: string | null;
  last_login_at: number | null;
  subscription_expiry: number | null;
  is_active: number;
  created_at: number;
}

type TabType = 'overview' | 'users' | 'payments' | 'features' | 'settings';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    todayRevenue: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [apiKeySaved, setApiKeySaved] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        adminGetUsers(),
        adminGetStats()
      ]);
      setUsers(usersRes.users);
      setStats(statsRes);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUser = async (userId: string, isActive: boolean) => {
    try {
      await adminUpdateUser(userId, { is_active: !isActive });
      loadData();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleChangeRole = async (userId: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'editor' : 'admin';
    try {
      await adminUpdateUser(userId, { role: newRole });
      loadData();
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={48} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-sm text-slate-500">CLIPD Management</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 text-slate-700 hover:text-blue-600 font-medium"
          >
            Rudi Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <LayoutDashboard className="inline w-4 h-4 mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'users'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className="inline w-4 h-4 mr-2" />
            Watumiaji
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'payments'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <CreditCard className="inline w-4 h-4 mr-2" />
            Malipo
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'features'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Shield className="inline w-4 h-4 mr-2" />
            Features
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'settings'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Settings className="inline w-4 h-4 mr-2" />
            Mipangilio
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Dashboard Overview</h2>
              <p className="text-slate-600">Muhtasari wa platform yote</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 shadow-lg text-white">
                <div className="flex items-center gap-3 mb-2">
                  <Users size={24} />
                  <span className="text-sm opacity-90">Jumla ya Watumiaji</span>
                </div>
                <div className="text-4xl font-bold">{stats.totalUsers}</div>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 shadow-lg text-white">
                <div className="flex items-center gap-3 mb-2">
                  <Activity size={24} />
                  <span className="text-sm opacity-90">Wanaotumia Sasa</span>
                </div>
                <div className="text-4xl font-bold">{stats.activeUsers}</div>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 shadow-lg text-white">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign size={24} />
                  <span className="text-sm opacity-90">Mapato Leo</span>
                </div>
                <div className="text-3xl font-bold">TSh {stats.todayRevenue.toLocaleString()}</div>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 shadow-lg text-white">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp size={24} />
                  <span className="text-sm opacity-90">Mapato Yote</span>
                </div>
                <div className="text-3xl font-bold">TSh {stats.totalRevenue.toLocaleString()}</div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Watumiaji Wapya (Siku 7)</h3>
                <div className="space-y-3">
                  {users.slice(0, 5).map((u) => (
                    <div key={u.id} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{u.name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {u.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Platform Stats</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Watumiaji Walipia (Leo)</span>
                    <span className="font-bold text-slate-900">{stats.activeUsers}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Watumiaji Hawajalipia</span>
                    <span className="font-bold text-slate-900">{stats.totalUsers - stats.activeUsers}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Bei kwa Siku</span>
                    <span className="font-bold text-slate-900">TSh 5,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Subscription Duration</span>
                    <span className="font-bold text-slate-900">24 masaa</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'users' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="text-blue-600" size={24} />
                  <span className="text-sm text-slate-500">Total Users</span>
                </div>
                <div className="text-3xl font-bold text-slate-900">{stats.totalUsers}</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <Activity className="text-green-600" size={24} />
                  <span className="text-sm text-slate-500">Active Users</span>
                </div>
                <div className="text-3xl font-bold text-slate-900">{stats.activeUsers}</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="text-orange-600" size={24} />
                  <span className="text-sm text-slate-500">Today Revenue</span>
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  TSh {stats.todayRevenue.toLocaleString()}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="text-purple-600" size={24} />
                  <span className="text-sm text-slate-500">Total Revenue</span>
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  TSh {stats.totalRevenue.toLocaleString()}
                </div>
              </div>
            </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Watumiaji Wote</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Jina
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Simu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {users.map((u) => {
                  const isExpired = !u.subscription_expiry || u.subscription_expiry < Date.now();
                  
                  return (
                    <tr key={u.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 text-sm text-slate-900">{u.name}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{u.phone}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleChangeRole(u.id, u.role)}
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            u.role === 'admin'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {u.role}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {isExpired ? (
                          <span className="text-red-600">Expired</span>
                        ) : (
                          <span className="text-green-600">
                            Active ({Math.ceil((u.subscription_expiry! - Date.now()) / (1000 * 60 * 60))}h)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.is_active === 1
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {u.is_active === 1 ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleUser(u.id, u.is_active === 1)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            u.is_active === 1
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {u.is_active === 1 ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}

        {activeTab === 'payments' && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Malipo Yote</h2>
              <p className="text-slate-600">Historia ya malipo ya watumiaji wote</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Recent Payments</h3>
              </div>
              <div className="p-6">
                <p className="text-slate-600 text-sm mb-4">
                  Malipo yanahifadhiwa kwenye D1 database na kusync na HarakaPay webhook.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Payment Flow:</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>User anabonyeza "Lipia TSh 5,000"</li>
                    <li>API inatuma USSD push kwenda simu ya user (HarakaPay)</li>
                    <li>User anaingiza PIN kwenye simu</li>
                    <li>HarakaPay inatuma webhook notification</li>
                    <li>API inaupdate subscription_expiry (+24 hours)</li>
                    <li>User anafungua features kwa masaa 24</li>
                  </ol>
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-700 mb-1">Mapato Leo</p>
                    <p className="text-2xl font-bold text-green-900">TSh {stats.todayRevenue.toLocaleString()}</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-purple-700 mb-1">Mapato Yote</p>
                    <p className="text-2xl font-bold text-purple-900">TSh {stats.totalRevenue.toLocaleString()}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700 mb-1">Malipo ya Leo</p>
                    <p className="text-2xl font-bold text-blue-900">{stats.activeUsers}</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'features' && (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Features za Platform</h2>
              <p className="text-slate-600">Features zote zinazopatikana kwenye CLIPD</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Video className="text-blue-600" size={24} />
                  Video Processing
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>FFmpeg.WASM (browser-based)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>FFmpeg Native (Tauri desktop)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Export 9:16, 1:1, 16:9</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>100% local processing</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Activity className="text-green-600" size={24} />
                  AI Transcription
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Whisper.WASM (browser)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Whisper.cpp Native (desktop)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Kiswahili support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Word-level timestamps</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="text-purple-600" size={24} />
                  Virality Scoring
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Emotion detection (40%)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Numbers/Stats (30%)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Brevity score (25%)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Keyword matching (25%)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <CreditCard className="text-orange-600" size={24} />
                  Payment System
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>HarakaPay integration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>M-Pesa, Tigo Pesa, Airtel Money</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>USSD push (automatic)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Daily subscription (TSh 5,000/24hrs)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Shield className="text-red-600" size={24} />
                  Security & Privacy
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Video 100% local (hakuna upload)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Device fingerprinting (single device)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>JWT authentication (24hrs)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>No account sharing</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <LayoutDashboard className="text-indigo-600" size={24} />
                  Admin Features
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>User management (enable/disable)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Revenue tracking (daily/total)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>Payment history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600">✓</span>
                    <span>HarakaPay settings</span>
                  </li>
                </ul>
              </div>
            </div>
          </>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">
              Mipangilio ya Malipo (HarakaPay)
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  HarakaPay API Key
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    setApiKeySaved(false);
                  }}
                  className="w-full px-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400"
                  placeholder="hpk_..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  API key imeset kwenye Cloudflare Workers secrets. Badilisha hapa kwa update.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Taarifa za HarakaPay:</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ M-Pesa (Vodacom)</li>
                  <li>✓ Tigo Pesa</li>
                  <li>✓ Airtel Money</li>
                  <li>✓ Halotel HaloPesa</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 mb-2">Status ya System:</h3>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>✓ API Key: Imeset</li>
                  <li>✓ Webhook: Active</li>
                  <li>✓ Daily Price: TSh 5,000</li>
                  <li>✓ Duration: 24 hours</li>
                </ul>
              </div>

              <button
                onClick={() => {
                  // TODO: Implement API key update via wrangler API
                  setApiKeySaved(true);
                  alert('API key updated! (Note: Requires manual wrangler secret update for production)');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Hifadhi API Key
              </button>

              {apiKeySaved && (
                <p className="text-green-600 text-sm">
                  ✓ API key imehifadhiwa!
                </p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
